import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import JsBarcode from 'jsbarcode';
import ModalHabilitations from './ModalHabilitations';
import { formatDuree, decomposerSecondes, composerSecondes } from '../utils/formatDuree';

function GestionProduits() {
  const [produits, setProduits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduit, setEditingProduit] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    delai_max_jours: ''
  });
  const [operations, setOperations] = useState([]);
  const [showOperationsModal, setShowOperationsModal] = useState(false);
  const [selectedProduit, setSelectedProduit] = useState(null);

  // ---- Nomenclature ----
  const [showNomencModal, setShowNomencModal]             = useState(false);
  const [selectedProduitNomenc, setSelectedProduitNomenc] = useState(null);

  useEffect(() => {
    loadProduits();
  }, []);

  const loadProduits = async () => {
    const { data, error } = await supabase
      .from('types_produits')
      .select('*, operations(temps_realisation)')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Erreur chargement produits:', error);
    } else {
      setProduits(data || []);
    }
  };

  const loadOperations = async (produitId) => {
    const { data, error } = await supabase
      .from('operations')
      .select('*')
      .eq('type_produit_id', produitId)
      .order('ordre', { ascending: true });
    
    if (error) {
      console.error('Erreur chargement opérations:', error);
    } else {
      setOperations(data || []);
    }
  };

  const generateCodeBarre = () => {
    // Génère un code plus court : PROD suivi de 8 caractères
    // Alphabet compatible QWERTY/AZERTY (sans A, M, Q, W, Z qui changent entre les deux layouts)
    // Également sans O, I pour éviter confusion avec 0, 1
    const chars = '0123456789BCDEFGHJKLNPRSTUVXY';
    let code = 'PROD';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handlePrint = (produit) => {
    // Créer un canvas temporaire pour générer le code-barre
    const canvas = document.createElement('canvas');
    
    try {
      JsBarcode(canvas, produit.code_barre, {
        format: 'CODE128',
        width: 3,
        height: 70,
        displayValue: false,
        margin: 0
      });
      
      const barcodeDataUrl = canvas.toDataURL('image/png');
      
      const printWindow = window.open('', '', 'width=400,height=200');
      const content = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impression - ${produit.nom}</title>
            <style>
              @page {
                size: 89mm 36mm;
                margin: 0;
              }
              
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              body {
                font-family: Arial, sans-serif;
                width: 89mm;
                height: 36mm;
                margin: 0;
                padding: 2mm 2mm;
                overflow: hidden;
              }
              
              .label-container {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
              }
              
              .barcode-section {
                text-align: center;
                margin-bottom: 0.5mm;
              }
              
              .barcode-image {
                width: 70mm;
                height: auto;
                max-height: 14mm;
                margin: 0 auto 0.5mm auto;
                display: block;
              }
              
              .code-text {
                font-family: 'Courier New', monospace;
                font-size: 8pt;
                font-weight: bold;
                color: #000;
                letter-spacing: 0.5px;
              }
              
              .product-info {
                text-align: center;
              }
              
              .name {
                font-size: 10pt;
                font-weight: bold;
                color: #000;
                margin-bottom: 0.5mm;
              }
              
              @media print {
                body {
                  width: 89mm;
                  height: 36mm;
                  padding: 2mm 2mm;
                }
              }
            </style>
          </head>
          <body>
            <div class="label-container">
              <div class="barcode-section">
                <img src="${barcodeDataUrl}" class="barcode-image" alt="Barcode"/>
                <div class="code-text">${produit.code_barre}</div>
              </div>
              
              <div class="product-info">
                <div class="name">${produit.nom}</div>
              </div>
            </div>
            
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                }, 250);
              };
            </script>
          </body>
        </html>
      `;
      
      printWindow.document.write(content);
      printWindow.document.close();
    } catch (error) {
      console.error('Erreur lors de la génération du code-barre:', error);
      alert('Erreur lors de la génération du code-barre');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (editingProduit) {
      const { error } = await supabase
        .from('types_produits')
        .update(formData)
        .eq('id', editingProduit.id);
      
      if (error) {
        console.error('Erreur mise à jour:', error);
      } else {
        loadProduits();
        closeModal();
      }
    } else {
      const codeBarre = generateCodeBarre();
      const dataToInsert = { ...formData, code_barre: codeBarre };
      
      const { error } = await supabase
        .from('types_produits')
        .insert([dataToInsert]);
      
      if (error) {
        console.error('Erreur création:', error);
      } else {
        loadProduits();
        closeModal();
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      const { error } = await supabase
        .from('types_produits')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Erreur suppression:', error);
      } else {
        loadProduits();
      }
    }
  };

  const openModal = (produit = null) => {
    if (produit) {
      setEditingProduit(produit);
      setFormData({
        nom: produit.nom,
        delai_max_jours: produit.delai_max_jours
      });
    } else {
      setEditingProduit(null);
      setFormData({
        nom: '',
        delai_max_jours: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduit(null);
    setFormData({
      nom: '',
      delai_max_jours: ''
    });
  };

  const openNomenclatureModal = (produit) => {
    setSelectedProduitNomenc(produit);
    setShowNomencModal(true);
  };

  const openOperationsModal = async (produit) => {
    setSelectedProduit(produit);
    await loadOperations(produit.id);
    setShowOperationsModal(true);
  };

  const closeOperationsModal = () => {
    setShowOperationsModal(false);
    setSelectedProduit(null);
    setOperations([]);
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '40px'
      }}>
        <div>
          <h1 style={{ 
            color: '#212529',
            fontSize: '32px',
            fontWeight: '600',
            margin: 0,
            marginBottom: '8px'
          }}>
            Gestion des Produits
          </h1>
          <p style={{ 
            color: '#6c757d',
            fontSize: '15px',
            margin: 0
          }}>
            Créez et gérez vos types de produits et leurs opérations
          </p>
        </div>
        <button
          onClick={() => openModal()}
          style={{
            padding: '14px 32px',
            backgroundColor: '#1aabf9',
            color: '#ffffff',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(26, 171, 249, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 25px rgba(26, 171, 249, 0.4)';
            e.target.style.backgroundColor = '#0d96e6';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(26, 171, 249, 0.3)';
            e.target.style.backgroundColor = '#1aabf9';
          }}
        >
          + Nouveau Produit
        </button>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
        gap: '24px'
      }}>
        {produits.map(produit => (
          <div
            key={produit.id}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '15px',
              padding: '28px',
              border: '1px solid #e0e5ea',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.border = '1px solid #1aabf9';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(26, 171, 249, 0.15)';
              e.currentTarget.style.transform = 'translateY(-5px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.border = '1px solid #e0e5ea';
              e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <h3 style={{ 
              color: '#212529',
              marginTop: 0,
              marginBottom: '16px',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              {produit.nom}
            </h3>
            
            {produit.code_barre && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{
                  fontSize: '10px',
                  color: '#6c757d',
                  marginBottom: '4px',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Code-barre
                </div>
                <div style={{
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  color: '#212529',
                  fontWeight: '700',
                  letterSpacing: '1px'
                }}>
                  {produit.code_barre}
                </div>
              </div>
            )}
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#1aabf9'
              }}></div>
              <span style={{ 
                color: '#6c757d',
                fontSize: '14px'
              }}>
                Délai maximum : <span style={{ color: '#1aabf9', fontWeight: '600' }}>
                  {produit.delai_max_jours} jours
                </span>
              </span>
              {produit.operations && produit.operations.length > 0 && (
                <>
                  <span style={{ color: '#dee2e6', fontSize: '14px' }}>•</span>
                  <span style={{ 
                    color: '#6c757d',
                    fontSize: '14px'
                  }}>
                    Temps de production : <span style={{ color: '#28a745', fontWeight: '600' }}>
                      {formatDuree(produit.operations.reduce((sum, op) => sum + (op.temps_realisation || 0), 0))}
                    </span>
                  </span>
                </>
              )}
            </div>
            <div style={{ 
              display: 'flex', 
              gap: '10px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => openOperationsModal(produit)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#1aabf9',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0d96e6';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#1aabf9';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Opérations
              </button>
              <button
                onClick={() => openNomenclatureModal(produit)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#1a7f4e',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#145a38';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#1a7f4e';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Composants
              </button>
              <button
                onClick={() => handlePrint(produit)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: '#ff9800',
                  border: '1px solid #ff9800',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#ff9800';
                  e.target.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#ff9800';
                }}
              >
                🖨️ Imprimer
              </button>
              <button
                onClick={() => openModal(produit)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: '#6c757d',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.color = '#212529';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6c757d';
                }}
              >
                Modifier
              </button>
              <button
                onClick={() => handleDelete(produit.id)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: '#dc3545',
                  border: '1px solid #dc3545',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#dc3545';
                  e.target.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#dc3545';
                }}
              >
                Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>

      {produits.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '80px 20px',
          color: '#6c757d',
          fontSize: '15px',
          backgroundColor: '#ffffff',
          borderRadius: '15px',
          border: '2px dashed #dee2e6'
        }}>
          Aucun produit créé. Cliquez sur "Nouveau Produit" pour commencer.
        </div>
      )}

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '20px',
            padding: '40px',
            width: '520px',
            maxWidth: '90%',
            border: '2px solid #1aabf9',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{ 
              color: '#212529',
              marginTop: 0,
              marginBottom: '30px',
              fontSize: '24px',
              fontWeight: '600'
            }}>
              {editingProduit ? 'Modifier le produit' : 'Nouveau produit'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block',
                  color: '#495057',
                  marginBottom: '10px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Nom du produit
                </label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    backgroundColor: '#f8f9fa',
                    color: '#212529',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    fontSize: '15px',
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '2px solid #1aabf9';
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #dee2e6';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                />
              </div>
              <div style={{ marginBottom: '32px' }}>
                <label style={{ 
                  display: 'block',
                  color: '#495057',
                  marginBottom: '10px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Délai maximum (jours)
                </label>
                <input
                  type="number"
                  value={formData.delai_max_jours}
                  onChange={(e) => setFormData({...formData, delai_max_jours: e.target.value})}
                  required
                  min="1"
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    backgroundColor: '#f8f9fa',
                    color: '#212529',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    fontSize: '15px',
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '2px solid #1aabf9';
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #dee2e6';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    padding: '12px 28px',
                    backgroundColor: 'transparent',
                    color: '#6c757d',
                    border: '1px solid #dee2e6',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.color = '#212529';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6c757d';
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 28px',
                    backgroundColor: '#1aabf9',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#0d96e6';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#1aabf9';
                  }}
                >
                  {editingProduit ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showOperationsModal && selectedProduit && (
        <ModalOperations 
          produit={selectedProduit}
          operations={operations}
          onClose={closeOperationsModal}
          onReload={() => loadOperations(selectedProduit.id)}
        />
      )}

      {showNomencModal && selectedProduitNomenc && (
        <ModalNomenclature
          produit={selectedProduitNomenc}
          onClose={() => { setShowNomencModal(false); setSelectedProduitNomenc(null); }}
        />
      )}
    </div>
  );
}

function ModalOperations({ produit, operations, onClose, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [editingOp, setEditingOp] = useState(null);
  const [showHabilitations, setShowHabilitations] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    temps_minutes: '',
    temps_secondes: '',
    cout_fixe: '',
    inclus_dans_planning: true
  });

  const generateCodeBarre = () => {
    // Génère un code plus court : OP suivi de 10 caractères
    // Alphabet compatible QWERTY/AZERTY (sans A, M, Q, W, Z qui changent entre les deux layouts)
    // Également sans O, I pour éviter confusion avec 0, 1
    const chars = '0123456789BCDEFGHJKLNPRSTUVXY';
    let code = 'OP';
    for (let i = 0; i < 10; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handlePrintOperation = (operation) => {
    const canvas = document.createElement('canvas');
    
    try {
      JsBarcode(canvas, operation.code_barre, {
        format: 'CODE128',
        width: 3,
        height: 70,
        displayValue: false,
        margin: 0
      });
      
      const barcodeDataUrl = canvas.toDataURL('image/png');
      
      const printWindow = window.open('', '', 'width=400,height=200');
      const content = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impression - ${operation.nom}</title>
            <style>
              @page {
                size: 89mm 36mm;
                margin: 0;
              }
              
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              body {
                font-family: Arial, sans-serif;
                width: 89mm;
                height: 36mm;
                margin: 0;
                padding: 2mm 2mm;
                overflow: hidden;
              }
              
              .label-container {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
              }
              
              .barcode-section {
                text-align: center;
                margin-bottom: 0.5mm;
              }
              
              .barcode-image {
                width: 70mm;
                height: auto;
                max-height: 14mm;
                margin: 0 auto 0.5mm auto;
                display: block;
              }
              
              .code-text {
                font-family: 'Courier New', monospace;
                font-size: 8pt;
                font-weight: bold;
                color: #000;
                letter-spacing: 0.5px;
              }
              
              .operation-info {
                text-align: center;
              }
              
              .name {
                font-size: 10pt;
                font-weight: bold;
                color: #000;
                margin-bottom: 0.5mm;
              }
              
              @media print {
                body {
                  width: 89mm;
                  height: 36mm;
                  padding: 2mm 2mm;
                }
              }
            </style>
          </head>
          <body>
            <div class="label-container">
              <div class="barcode-section">
                <img src="${barcodeDataUrl}" class="barcode-image" alt="Barcode"/>
                <div class="code-text">${operation.code_barre}</div>
              </div>
              
              <div class="operation-info">
                <div class="name">${operation.nom}</div>
                <div style="font-size: 8pt; color: #666; margin-top: 0.5mm;">${produit.nom}</div>
              </div>
            </div>
            
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                }, 250);
              };
            </script>
          </body>
        </html>
      `;
      
      printWindow.document.write(content);
      printWindow.document.close();
    } catch (error) {
      console.error('Erreur lors de la génération du code-barre:', error);
      alert('Erreur lors de la génération du code-barre');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const tempsEnSecondes = composerSecondes(formData.temps_minutes, formData.temps_secondes);
    if (tempsEnSecondes <= 0) {
      alert('Le temps de réalisation doit être supérieur à 0');
      return;
    }

    const dataToSend = {
      nom: formData.nom,
      temps_realisation: tempsEnSecondes,
      cout_fixe: formData.cout_fixe,
      inclus_dans_planning: formData.inclus_dans_planning,
      type_produit_id: produit.id,
      ordre: editingOp ? editingOp.ordre : operations.length + 1
    };

    if (editingOp) {
      const { error } = await supabase.from('operations').update(dataToSend).eq('id', editingOp.id);
      if (!error) { onReload(); closeForm(); }
    } else {
      // Ajouter un code-barre pour les nouvelles opérations
      const codeBarre = generateCodeBarre();
      dataToSend.code_barre = codeBarre;
      
      const { error } = await supabase.from('operations').insert([dataToSend]);
      if (!error) { onReload(); closeForm(); }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cette opération ?')) {
      const { error } = await supabase.from('operations').delete().eq('id', id);
      if (!error) onReload();
    }
  };

  const openForm = (operation = null) => {
    if (operation) {
      setEditingOp(operation);
      const { minutes, secondes } = decomposerSecondes(operation.temps_realisation);
      setFormData({
        nom: operation.nom,
        temps_minutes: minutes,
        temps_secondes: secondes,
        cout_fixe: operation.cout_fixe || '',
        inclus_dans_planning: operation.inclus_dans_planning
      });
    } else {
      setEditingOp(null);
      setFormData({ nom: '', temps_minutes: '', temps_secondes: '', cout_fixe: '', inclus_dans_planning: true });
    }
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditingOp(null); };

  const openHabilitations = (operation) => {
    setSelectedOperation(operation);
    setShowHabilitations(true);
  };

  const closeHabilitations = () => {
    setShowHabilitations(false);
    setSelectedOperation(null);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 }}>
      <div style={{ backgroundColor: '#ffffff', borderRadius: '20px', padding: '40px', width: '900px', maxWidth: '90%', maxHeight: '90vh', overflow: 'auto', border: '2px solid #1aabf9', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <h2 style={{ color: '#212529', margin: 0, fontSize: '24px', fontWeight: '600' }}>Opérations - {produit.nom}</h2>
          <button onClick={onClose} style={{ padding: '10px 20px', backgroundColor: 'transparent', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.3s ease' }} onMouseEnter={(e) => { e.target.style.backgroundColor = '#f8f9fa'; e.target.style.color = '#212529'; }} onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#6c757d'; }}>Fermer</button>
        </div>

        <button onClick={() => openForm()} style={{ padding: '12px 24px', backgroundColor: '#1aabf9', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: 'pointer', marginBottom: '24px', fontWeight: '600', fontSize: '14px', transition: 'all 0.3s ease' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#0d96e6'} onMouseLeave={(e) => e.target.style.backgroundColor = '#1aabf9'}>+ Ajouter une opération</button>

        <div style={{ marginTop: '24px' }}>
          {operations.map((op, index) => (
            <div key={op.id} style={{ backgroundColor: '#f8f9fa', padding: '24px', marginBottom: '12px', borderRadius: '12px', border: '1px solid #e0e5ea', transition: 'all 0.3s ease' }} onMouseEnter={(e) => e.currentTarget.style.border = '1px solid #1aabf9'} onMouseLeave={(e) => e.currentTarget.style.border = '1px solid #e0e5ea'}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#212529', fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>Étape {index + 1} : {op.nom}</div>
                  <div style={{ color: '#6c757d', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                    <span style={{ color: '#1aabf9', fontWeight: '600' }}>{formatDuree(op.temps_realisation)}</span>
                    <span>•</span>
                    <span><span style={{ color: '#1aabf9', fontWeight: '600' }}>{op.cout_fixe || 0}</span>€</span>
                    {op.inclus_dans_planning ? (<><span>•</span><span style={{ color: '#28a745' }}>📅 Inclus planning</span></>) : (<><span>•</span><span style={{ color: '#999' }}>⊘ Hors planning</span></>)}
                    {op.code_barre && (
                      <>
                        <span>•</span>
                        <span style={{ fontFamily: 'monospace', fontWeight: '600', color: '#495057' }}>
                          Code: {op.code_barre}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button onClick={() => openHabilitations(op)} style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#9c27b0', border: '1px solid #9c27b0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.3s ease' }} onMouseEnter={(e) => { e.target.style.backgroundColor = '#9c27b0'; e.target.style.color = '#ffffff'; }} onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#9c27b0'; }}>🔐 Habilitations</button>
                  {op.code_barre && (
                    <button onClick={() => handlePrintOperation(op)} style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#ff9800', border: '1px solid #ff9800', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.3s ease' }} onMouseEnter={(e) => { e.target.style.backgroundColor = '#ff9800'; e.target.style.color = '#ffffff'; }} onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#ff9800'; }}>🖨️ Imprimer</button>
                  )}
                  <button onClick={() => openForm(op)} style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.3s ease' }} onMouseEnter={(e) => { e.target.style.backgroundColor = '#ffffff'; e.target.style.color = '#212529'; }} onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#6c757d'; }}>Modifier</button>
                  <button onClick={() => handleDelete(op.id)} style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.3s ease' }} onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc3545'; e.target.style.color = '#ffffff'; }} onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#dc3545'; }}>Supprimer</button>
                </div>
              </div>
            </div>
          ))}
          {operations.length === 0 && (<div style={{ textAlign: 'center', color: '#6c757d', padding: '40px', backgroundColor: '#f8f9fa', borderRadius: '12px', border: '2px dashed #dee2e6' }}>Aucune opération définie</div>)}
        </div>

        {showForm && (
          <div style={{ marginTop: '32px', padding: '32px', backgroundColor: '#f8f9fa', borderRadius: '12px', border: '2px solid #1aabf9' }}>
            <h3 style={{ color: '#212529', marginTop: 0, marginBottom: '24px', fontSize: '20px', fontWeight: '600' }}>{editingOp ? 'Modifier l\'opération' : 'Nouvelle opération'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Nom de l'opération</label>
                <input type="text" value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})} required style={{ width: '100%', padding: '12px 16px', backgroundColor: '#ffffff', color: '#212529', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'all 0.3s ease' }} onFocus={(e) => { e.target.style.border = '2px solid #1aabf9'; }} onBlur={(e) => { e.target.style.border = '1px solid #dee2e6'; }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Temps de réalisation</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#6c757d', marginBottom: '4px', fontSize: '12px' }}>Minutes</label>
                    <input type="number" value={formData.temps_minutes} onChange={(e) => setFormData({...formData, temps_minutes: e.target.value})} min="0" placeholder="0" style={{ width: '100%', padding: '12px 16px', backgroundColor: '#ffffff', color: '#212529', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'all 0.3s ease' }} onFocus={(e) => { e.target.style.border = '2px solid #1aabf9'; }} onBlur={(e) => { e.target.style.border = '1px solid #dee2e6'; }} />
                  </div>
                  <span style={{ color: '#6c757d', fontSize: '18px', fontWeight: '600', marginTop: '20px' }}>:</span>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#6c757d', marginBottom: '4px', fontSize: '12px' }}>Secondes</label>
                    <input type="number" value={formData.temps_secondes} onChange={(e) => setFormData({...formData, temps_secondes: Math.min(59, Math.max(0, parseInt(e.target.value) || 0)).toString()})} min="0" max="59" placeholder="0" style={{ width: '100%', padding: '12px 16px', backgroundColor: '#ffffff', color: '#212529', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'all 0.3s ease' }} onFocus={(e) => { e.target.style.border = '2px solid #1aabf9'; }} onBlur={(e) => { e.target.style.border = '1px solid #dee2e6'; }} />
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Coût fixe (€)</label>
                <input type="number" step="0.01" value={formData.cout_fixe} onChange={(e) => setFormData({...formData, cout_fixe: e.target.value})} style={{ width: '100%', padding: '12px 16px', backgroundColor: '#ffffff', color: '#212529', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'all 0.3s ease' }} onFocus={(e) => { e.target.style.border = '2px solid #1aabf9'; }} onBlur={(e) => { e.target.style.border = '1px solid #dee2e6'; }} />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', color: '#495057', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                  <input type="checkbox" checked={formData.inclus_dans_planning} onChange={(e) => setFormData({...formData, inclus_dans_planning: e.target.checked})} style={{ marginRight: '12px', width: '18px', height: '18px', cursor: 'pointer', accentColor: '#1aabf9' }} />
                  📅 Comptabiliser dans le planning de production
                </label>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeForm} style={{ padding: '12px 24px', backgroundColor: 'transparent', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.3s ease' }} onMouseEnter={(e) => { e.target.style.backgroundColor = '#ffffff'; e.target.style.color = '#212529'; }} onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#6c757d'; }}>Annuler</button>
                <button type="submit" style={{ padding: '12px 24px', backgroundColor: '#1aabf9', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.3s ease' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#0d96e6'} onMouseLeave={(e) => e.target.style.backgroundColor = '#1aabf9'}>{editingOp ? 'Mettre à jour' : 'Ajouter'}</button>
              </div>
            </form>
          </div>
        )}
        
        {showHabilitations && selectedOperation && (
          <ModalHabilitations 
            operation={selectedOperation}
            onClose={closeHabilitations}
          />
        )}
      </div>
    </div>
  );
}

export default GestionProduits;

// ============================================================
// MODAL NOMENCLATURE — BOM (Bill of Materials) d'un produit
// ============================================================
function ModalNomenclature({ produit, onClose }) {
  const [lignes, setLignes]       = useState([]);
  const [articles, setArticles]   = useState([]);
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('composants');

  // Formulaire ajout
  const [formArticleId, setFormArticleId] = useState('');
  const [formQte, setFormQte]             = useState('');
  const [formUnite, setFormUnite]         = useState('');
  const [formNotes, setFormNotes]         = useState('');
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');

  // Simulation
  const [simulQte, setSimulQte] = useState('1');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const [nomRes, artRes, stockRes] = await Promise.all([
      supabase.from('stock_nomenclature')
        .select('*, stock_articles(id, reference, designation, mode_gestion, unite_stock, unite_consommation, contenance, prix_achat_ht, type_prix)')
        .eq('type_produit_id', produit.id)
        .order('created_at', { ascending: true }),
      supabase.from('stock_articles')
        .select('id, reference, designation, mode_gestion, unite_stock, unite_consommation, contenance, prix_achat_ht, type_prix')
        .eq('actif', true).order('designation'),
      supabase.from('v_stock_courant')
        .select('article_id, items_en_stock, items_en_atelier, quantite_en_stock, quantite_en_atelier, unite_stock, unite_consommation, mode_gestion')
    ]);
    setLignes(nomRes.data || []);
    setArticles(artRes.data || []);
    setStockData(stockRes.data || []);
    setLoading(false);
  };

  const handleArticleSelect = (id) => {
    setFormArticleId(id);
    const art = articles.find(a => a.id === id);
    if (!art) { setFormUnite(''); return; }
    // Pour SUIVI : toujours en unité de consommation (m, mL...)
    // Pour SORTIE sans contenance : en unité de stock (cartouche, pièce)
    // Pour SORTIE avec contenance (sachets) : en sous-unité individuelle (pièce par défaut)
    if (art.mode_gestion === 'SUIVI') {
      setFormUnite(art.unite_consommation || art.unite_stock);
    } else {
      setFormUnite(art.contenance && parseFloat(art.contenance) > 1 ? 'pièce' : art.unite_stock);
    }
  };

  const handleAdd = async () => {
    if (!formArticleId) { setError('Sélectionnez un article.'); return; }
    const qte = parseFloat(formQte);
    if (!qte || qte <= 0) { setError('Saisissez une quantité valide.'); return; }
    setSaving(true); setError('');
    const { error: err } = await supabase.from('stock_nomenclature').insert([{
      type_produit_id:    produit.id,
      article_id:         formArticleId,
      quantite_par_unite: qte,
      unite:              formUnite.trim() || null,
      notes:              formNotes.trim() || null
    }]);
    if (err) {
      setError(err.message.includes('unique') ? 'Cet article est déjà dans les composants.' : 'Erreur : ' + err.message);
    } else {
      setFormArticleId(''); setFormQte(''); setFormUnite(''); setFormNotes('');
      loadAll();
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await supabase.from('stock_nomenclature').delete().eq('id', id);
    loadAll();
  };

  // Calcul coût matière
  const calcCout = (ligne) => {
    const art = ligne.stock_articles;
    if (!art?.prix_achat_ht) return null;
    const prix = parseFloat(art.prix_achat_ht);
    const qte  = parseFloat(ligne.quantite_par_unite);
    if (isNaN(prix) || isNaN(qte) || prix <= 0) return null;
    if (art.type_prix === 'PAR_UNITE_CONSO') return qte * prix;
    // PAR_UNITE_STOCK : diviser par la contenance pour obtenir le coût
    const contenance = art.contenance ? parseFloat(art.contenance) : 1;
    return (qte / contenance) * prix;
  };
  const totalCout = lignes.reduce((sum, l) => { const c = calcCout(l); return c ? sum + c : sum; }, 0);

  // Calcul simulation
  const n = Math.max(1, parseInt(simulQte, 10) || 1);
  const simulData = lignes.map(ligne => {
    const art = ligne.stock_articles;
    const needed = parseFloat((ligne.quantite_par_unite * n).toFixed(4));
    const s = stockData.find(x => x.article_id === art?.id);
    const available = s
      ? (art.mode_gestion === 'SUIVI' ? (s.quantite_en_stock || 0) : (s.items_en_stock || 0))
      : 0;
    const manquant = Math.max(0, parseFloat((needed - available).toFixed(4)));
    return { ...ligne, needed, available, manquant, ok: manquant === 0 };
  });
  const canProduceAll = simulData.length > 0 && simulData.every(s => s.ok);

  // Styles internes
  const inp = { width: '100%', padding: '8px 11px', border: '1px solid #dee2e6', borderRadius: '7px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '12px', fontWeight: '600', color: '#495057', marginBottom: '4px' };
  const bdg = (c, bg) => ({ display: 'inline-block', padding: '2px 9px', borderRadius: '16px', fontSize: '11px', fontWeight: '600', color: c, backgroundColor: bg });

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '860px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

        {/* Header */}
        <div style={{ padding: '22px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1, borderBottom: '1px solid #e8ecf1', paddingBottom: '16px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', color: '#1a2332' }}>📋 Composants</h2>
            <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '3px' }}>{produit.nom}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6c757d' }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '16px 28px 0', borderBottom: '2px solid #e8ecf1' }}>
          {[['composants', '🔩 Composants'], ['simulation', '🧮 Simulation stock']].map(([id, label]) => (
            <button key={id} onClick={() => setActiveTab(id)} style={{ padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: activeTab === id ? '700' : '500', backgroundColor: activeTab === id ? '#1a7f4e' : 'transparent', color: activeTab === id ? '#fff' : '#6c757d', borderRadius: '6px 6px 0 0', marginBottom: '-2px', borderBottom: activeTab === id ? '2px solid #1a7f4e' : '2px solid transparent' }}>
              {label}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', paddingBottom: '8px' }}>
            <span style={bdg('#6c757d', '#e9ecef')}>{lignes.length} composant{lignes.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <div style={{ padding: '20px 28px 28px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d' }}>Chargement...</div>
          ) : (
            <>
              {/* ============ ONGLET COMPOSANTS ============ */}
              {activeTab === 'composants' && (
                <div>
                  {/* Liste des lignes */}
                  {lignes.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d', backgroundColor: '#f8f9fa', borderRadius: '10px', marginBottom: '20px' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔩</div>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>Aucun composant défini</div>
                      <div style={{ fontSize: '13px' }}>Ajoutez les articles nécessaires à la fabrication de ce produit.</div>
                    </div>
                  ) : (
                    <div style={{ marginBottom: '24px', overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e8ecf1' }}>
                            {['Article', 'Référence', 'Mode', 'Qté / produit', 'Unité', 'Coût / produit', 'Notes', ''].map(h => (
                              <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {lignes.map((l, i) => {
                            const art = l.stock_articles;
                            return (
                              <tr key={l.id} style={{ borderBottom: '1px solid #f0f2f5', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                <td style={{ padding: '10px 12px', fontWeight: '600', color: '#1a2332' }}>{art?.designation || '—'}</td>
                                <td style={{ padding: '10px 12px' }}>
                                  <code style={{ backgroundColor: '#f0f2f5', padding: '2px 7px', borderRadius: '4px', fontSize: '11px' }}>{art?.reference}</code>
                                </td>
                                <td style={{ padding: '10px 12px' }}>
                                  {art?.mode_gestion === 'SUIVI'
                                    ? <span style={bdg('#1a5276', '#d6eaf8')}>SUIVI</span>
                                    : <span style={bdg('#5d6d7e', '#eaecee')}>SORTIE</span>}
                                </td>
                                <td style={{ padding: '10px 12px', fontWeight: '700', color: '#1a2332' }}>{l.quantite_par_unite}</td>
                                <td style={{ padding: '10px 12px', color: '#495057' }}>{l.unite || '—'}</td>
                                <td style={{ padding: '10px 12px' }}>
                                  {(() => { const c = calcCout(l); return c != null ? <span style={{ fontWeight: '600', color: '#1a7f4e' }}>{c.toFixed(4)} €</span> : <span style={{ color: '#ccc' }}>—</span>; })()}
                                </td>
                                <td style={{ padding: '10px 12px', color: '#6c757d', fontSize: '12px' }}>{l.notes || '—'}</td>
                                <td style={{ padding: '10px 12px' }}>
                                  <button onClick={() => handleDelete(l.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '16px', padding: '2px 6px' }} title="Supprimer">✕</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {totalCout > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 12px', backgroundColor: '#f0fff4', borderTop: '2px solid #1a7f4e', borderRadius: '0 0 6px 6px', fontSize: '13px' }}>
                          <span style={{ color: '#6c757d', marginRight: '12px' }}>Coût matière total / produit</span>
                          <span style={{ fontWeight: '800', color: '#1a7f4e', fontSize: '15px' }}>{totalCout.toFixed(4)} €</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Formulaire d'ajout */}
                  <div style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px', border: '1px solid #e8ecf1' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#495057', marginBottom: '14px' }}>➕ Ajouter un composant</div>
                    {error && <div style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '7px', padding: '10px 14px', color: '#dc3545', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '12px', alignItems: 'end' }}>
                      <div>
                        <label style={lbl}>Article <span style={{ color: '#dc3545' }}>*</span></label>
                        <select style={inp} value={formArticleId} onChange={e => handleArticleSelect(e.target.value)}>
                          <option value="">— Sélectionner —</option>
                          {articles.map(a => (
                            <option key={a.id} value={a.id} disabled={lignes.some(l => l.article_id === a.id)}>
                              {a.designation} ({a.reference}){lignes.some(l => l.article_id === a.id) ? ' ✓' : ''}
                            </option>
                          ))}
                        </select>
                        {formArticleId && (() => {
                          const art = articles.find(a => a.id === formArticleId);
                          if (!art) return null;
                          const isSuivi = art.mode_gestion === 'SUIVI';
                          const unite = isSuivi ? (art.unite_consommation || art.unite_stock) : (art.contenance && parseFloat(art.contenance) > 1 ? 'pièce' : art.unite_stock);
                          const formuleHint = art.type_prix === 'PAR_UNITE_CONSO'
                            ? `Coût = qté × ${art.prix_achat_ht || '?'} €/${unite}`
                            : art.contenance
                              ? `Coût = (qté / ${art.contenance}) × ${art.prix_achat_ht || '?'} €/${art.unite_stock}`
                              : `Coût = qté × ${art.prix_achat_ht || '?'} €/${art.unite_stock}`;
                          return (
                            <div style={{ fontSize: '11px', marginTop: '4px' }}>
                              <span style={{ color: '#1a7f4e', fontWeight: '600' }}>→ Unité utilisée : {unite}</span>
                              {art.prix_achat_ht && <span style={{ color: '#6c757d', marginLeft: '8px' }}>{formuleHint}</span>}
                            </div>
                          );
                        })()}
                      </div>
                      <div>
                        <label style={lbl}>Qté / produit <span style={{ color: '#dc3545' }}>*</span></label>
                        <input style={inp} type="number" min="0" step="0.001" value={formQte} onChange={e => setFormQte(e.target.value)} placeholder="Ex: 1.5" />
                      </div>
                      <div>
                        <label style={lbl}>Unité <span style={{ fontSize: '10px', color: '#1a7f4e', fontWeight: '600' }}>(auto-remplie)</span></label>
                        <input style={{ ...inp, backgroundColor: formArticleId ? '#f0fff4' : '#fff', borderColor: formArticleId ? '#c3e6cb' : '#dee2e6' }}
                          value={formUnite} onChange={e => setFormUnite(e.target.value)}
                          placeholder="Auto selon l'article" />
                      </div>
                      <div>
                        <label style={lbl}>Notes</label>
                        <input style={inp} value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Optionnel" />
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                      <button onClick={handleAdd} disabled={saving}
                        style={{ padding: '9px 20px', backgroundColor: '#1a7f4e', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                        {saving ? 'Ajout...' : '+ Ajouter le composant'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ============ ONGLET SIMULATION ============ */}
              {activeTab === 'simulation' && (
                <div>
                  {lignes.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>🧮</div>
                      <div style={{ fontWeight: '600' }}>Aucun composant dans la nomenclature</div>
                      <div style={{ fontSize: '13px', marginTop: '6px' }}>Ajoutez d'abord des composants dans l'onglet "Composants".</div>
                    </div>
                  ) : (
                    <>
                      {/* Saisie quantité */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px', backgroundColor: '#f0f8ff', borderRadius: '10px', padding: '14px 18px' }}>
                        <span style={{ fontWeight: '600', color: '#1a2332', fontSize: '14px' }}>Simuler la production de</span>
                        <input
                          type="number" min="1" value={simulQte}
                          onChange={e => setSimulQte(e.target.value)}
                          style={{ ...inp, width: '80px', fontSize: '18px', fontWeight: '700', textAlign: 'center' }}
                        />
                        <span style={{ fontWeight: '600', color: '#1a2332', fontSize: '14px' }}>{produit.nom}</span>
                        <div style={{ marginLeft: 'auto' }}>
                          {canProduceAll
                            ? <span style={bdg('#1a7f4e', '#d4edda')}>✅ Stock suffisant</span>
                            : <span style={bdg('#dc3545', '#f8d7da')}>⚠️ Stock insuffisant</span>}
                        </div>
                      </div>

                      {/* Tableau de simulation */}
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e8ecf1' }}>
                              {['Article', 'Qté nécessaire', 'Stock disponible', 'Manquant', 'Coût estimé', 'Statut'].map(h => (
                                <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {simulData.map((s, i) => {
                              const art = s.stock_articles;
                              return (
                                <tr key={s.id} style={{ borderBottom: '1px solid #f0f2f5', backgroundColor: s.ok ? (i % 2 === 0 ? '#fff' : '#fafbfc') : '#fff9f9' }}>
                                  <td style={{ padding: '11px 14px' }}>
                                    <div style={{ fontWeight: '600', color: '#1a2332' }}>{art?.designation}</div>
                                    <div style={{ fontSize: '11px', color: '#6c757d' }}>{art?.reference}</div>
                                  </td>
                                  <td style={{ padding: '11px 14px', fontWeight: '700', color: '#1a2332' }}>
                                    {s.needed} <span style={{ fontWeight: '400', color: '#6c757d', fontSize: '12px' }}>{s.unite}</span>
                                  </td>
                                  <td style={{ padding: '11px 14px' }}>
                                    <span style={{ fontWeight: '600', color: s.available >= s.needed ? '#1a7f4e' : '#dc3545' }}>
                                      {s.available}
                                    </span>
                                    <span style={{ color: '#6c757d', fontSize: '12px' }}> {s.unite}</span>
                                    {art?.mode_gestion === 'SORTIE' && (
                                      <div style={{ fontSize: '11px', color: '#6c757d' }}>({s.available} unités en stock)</div>
                                    )}
                                  </td>
                                  <td style={{ padding: '11px 14px' }}>
                                    {s.manquant > 0
                                      ? <span style={{ fontWeight: '700', color: '#dc3545' }}>−{s.manquant} {s.unite}</span>
                                      : <span style={{ color: '#6c757d' }}>—</span>}
                                  </td>
                                  <td style={{ padding: '11px 14px' }}>
                                    {(() => { const c = calcCout(s); const ct = c != null ? c * n : null; return ct != null ? <span style={{ fontWeight: '600', color: '#1a2332' }}>{ct.toFixed(2)} €</span> : <span style={{ color: '#ccc' }}>—</span>; })()}
                                  </td>
                                  <td style={{ padding: '11px 14px' }}>
                                    {s.ok
                                      ? <span style={bdg('#1a7f4e', '#d4edda')}>✅ OK</span>
                                      : <span style={bdg('#dc3545', '#f8d7da')}>⚠️ Insuffisant</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {!canProduceAll && (
                        <div style={{ marginTop: '16px', backgroundColor: '#fff9f9', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#856404' }}>
                          ⚠️ Le stock actuel ne permet pas de produire {n} × {produit.nom}. Vérifiez les articles en déficit et commandez auprès des fournisseurs concernés.
                        </div>
                      )}
                      {totalCout > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px', padding: '10px 14px', backgroundColor: '#f0fff4', border: '1px solid #c3e6cb', borderRadius: '8px', fontSize: '13px' }}>
                          <span style={{ color: '#6c757d', marginRight: '12px' }}>Coût matière pour {n} {produit.nom}</span>
                          <span style={{ fontWeight: '800', color: '#1a7f4e', fontSize: '15px' }}>{(totalCout * n).toFixed(2)} €</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e8ecf1' }}>
            <button onClick={onClose} style={{ padding: '10px 24px', backgroundColor: '#f8f9fa', color: '#495057', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Fermer</button>
          </div>
        </div>
      </div>
    </div>
  );
}